/**
 * CourseDocumentEditor — raw JSON editor for the course document.
 *
 * Mirrors ArticleDocumentEditor's role: lets the operator view and edit
 * the full document state. Uses a JSON editor since the course document
 * schema is still evolving and a schema-driven form would be premature.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useCourseContext } from './CourseContext';
import { courseRunsService } from '../../services/courseRunsService';

export function CourseDocumentEditor() {
  const { document: originalDoc, run, refreshDocument } = useCourseContext();

  const [content, setContent] = useState('');
  const [originalContent, setOriginalContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);

  useEffect(() => {
    if (originalDoc) {
      const formatted = JSON.stringify(originalDoc, null, 2);
      setContent(formatted);
      setOriginalContent(formatted);
      setJsonError(null);
    }
  }, [originalDoc]);

  const isDirty = content !== originalContent;

  const handleSave = useCallback(async () => {
    if (!run?.run_id) return;

    // Validate JSON
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(content);
      setJsonError(null);
    } catch (e: unknown) {
      setJsonError(e instanceof Error ? e.message : 'Invalid JSON');
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      await courseRunsService.updateDocument(run.run_id, parsed as any);
      await refreshDocument();
      setSavedAt(new Date().toLocaleTimeString());
    } catch (e: unknown) {
      setSaveError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [content, run?.run_id, refreshDocument]);

  if (!originalDoc) {
    return (
      <div className="p-4 text-sm text-gray-500">
        No document to edit. Run at least one stage first.
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between sticky top-0 bg-white z-10 py-2 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-semibold text-gray-900">Course Document</h3>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && <span className="text-xs text-green-600">Saved at {savedAt}</span>}
          {saveError && <span className="text-xs text-red-600">{saveError}</span>}
          {jsonError && <span className="text-xs text-red-600">JSON: {jsonError}</span>}
          <button
            type="button"
            className="px-3 py-1.5 text-sm rounded-md text-white hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: '#1a2332' }}
            disabled={saving || !isDirty}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      <textarea
        className="w-full border border-gray-300 rounded px-3 py-2 text-xs font-mono leading-relaxed focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
        rows={40}
        value={content}
        onChange={(e) => { setContent(e.target.value); setJsonError(null); }}
      />
    </div>
  );
}
